import { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/ApiConfig';
import { useNavigate } from 'react-router-dom';
import LoadingModal from './components/modals/LoadingModal';
import Pagination10 from './components/Pagination10';
import PageFooter from './components/PageFooter';
import useDefaultModal from './components/hooks/useDefaultModal';
import useConfirmModal from './components/hooks/useConfirmModal';
import ConfirmModal from './components/modals/ConfirmModal';
import DefaultModal from './components/modals/DefaultModal';
import usePagination10 from './components/hooks/usePagination10';
import Layout from './components/Layout';
import ResponsiveShadow from './components/ResponsiveShadow';
import PageHeader from './components/PageHeader';
import PageContent from './components/PageContent';
import Toolbar from './components/Toolbar';
import TableContainer from './components/TableContainer';
import TableHeader from './components/TableHeader';
import UsuarioModal from './components/modalServices/UsuarioModal';

function Usuarios() {
  const [Usuarios, setUsuarios] = useState([]);
  const [atualizar, setAtualizar] = useState([]);
  const [isLoading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState('');
  const [selectedUsuario, setSelectedUsuario] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUsuario, setEditingUsuario] = useState(null);
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const totalItems = Usuarios.length;
  const { setPagination } = usePagination10(totalItems);

  const normalizeString = str => {
    return str
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toUpperCase();
  };

  const usuariosFiltrados = Usuarios.filter(usu => {
    const normalizedBusca = normalizeString(filtro);
    return (
      normalizeString(usu.nome).includes(normalizedBusca) ||
      normalizeString(usu.cpf).includes(normalizedBusca) ||
      normalizeString(usu.login).includes(normalizedBusca)
    );
  });

  const selectedUsuarios = usuariosFiltrados.slice(
    setPagination.startIndex,
    setPagination.startIndex + setPagination.rowsPerPage
  );

  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const { setConfirmModal, showConfirmModal } = useConfirmModal();

  // Função para gerenciar seleção/desseleção de usuário
  const handleRowClick = usuario => {
    // Se clicar no mesmo usuário já selecionado, desmarca
    if (selectedUsuario && selectedUsuario.cpf === usuario.cpf) {
      setSelectedUsuario(null);
    } else {
      setSelectedUsuario(usuario);
    }
  };

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API_BASE_URL.USUARIO}/all`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(result => {
        setUsuarios(result.data);
      })
      .catch(error => {
        console.error('Erro ao obter usuários:', error);
        navigate('/');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [atualizar]);

  function handleDelete(cpf) {
    showConfirmModal('delete', () => handleConfirmDelete(cpf));
  }

  function handleConfirmDelete(cpf) {
    const loggedUser = JSON.parse(localStorage.getItem('user'));
    const loggedUserCpf = loggedUser?.cpf;

    if (cpf === loggedUserCpf) {
      showDefaultModal('error', 'Você não pode excluir a si mesmo.');
      return;
    }

    axios
      .get(`${API_BASE_URL.USUARIO_BIND}/exists_cpf/` + cpf, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(result => {
        if (result.data.exists) {
          showDefaultModal(
            'error',
            'Este usuário possui vínculos e não pode ser excluído. Exclua os vínculos antes de tentar novamente.'
          );
        } else {
          axios
            .delete(`${API_BASE_URL.USUARIO}/delete/` + cpf, {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            })
            .then(result => {
              setAtualizar(result);
              showDefaultModal(
                'success',
                'O registro foi excluído com sucesso!'
              );
            })
            .catch(error => {
              console.error('Erro ao excluir usuário:', error);
              showDefaultModal('error', 'Erro ao excluir o registro!');
            });
        }
      })
      .catch(error => {
        console.error('Erro ao verificar vínculos:', error);
        setTimeout(() => navigate('/'), 0);
      });
  }

  function handleOpenModal(usuario = null) {
    setEditingUsuario(usuario);
    setIsModalOpen(true);
  }

  function handleCloseModal() {
    setIsModalOpen(false);
    setEditingUsuario(null);
  }

  function handleModalSuccess() {
    setAtualizar(prev => !prev);
    showDefaultModal('success', 'Usuário salvo com sucesso!');
  }

  return (
    <Layout>
      <ResponsiveShadow>
        <PageHeader title="Usuários Cadastrados" icon="bi-people" closeRoute="/home" backButton={false} />
        <PageContent style={{ paddingBottom: 0 }}>
          <Toolbar
            selectedId={selectedUsuario ? selectedUsuario.cpf : null}
            onCreate={() => handleOpenModal()}
            onEdit={(id) => { if (id) handleOpenModal(Usuarios.find(u => u.cpf === id)); }}
            onDelete={(id) => { if (id) handleDelete(id); }}
            searchValue={filtro}
            onSearch={(v) => setFiltro((v||'').toUpperCase())}
            searchWidth="200px"
          >
            <button
              className="btn btn-info btn-sm botao-igual"
              disabled={!selectedUsuario}
              onClick={() => navigate('/usuario_vinculos', { state: { usuario: selectedUsuario } })}
            >
              <i className="bi bi-link-45deg me-1" />Vincular
            </button>
          </Toolbar>
          <LoadingModal loading={isLoading} />
      <TableContainer tableClass="table-custom-radius">
        <TableHeader columns={[
          { label: 'CPF', width: '120px' },
          { label: 'Nome' },
          { label: 'Login', width: '120px' },
          { label: 'Perfil', width: '130px' },
          { label: 'Situação', width: '100px' },
        ]} />
        <tbody>
          {!isLoading &&
            selectedUsuarios.map(usu => {
              const isSelected =
                selectedUsuario && selectedUsuario.cpf === usu.cpf;
              return (
                <tr
                  key={usu.cpf}
                  onClick={() => handleRowClick(usu)}
                  style={{ cursor: 'pointer' }}
                  className={isSelected ? 'table-active' : ''}
                  title={
                    isSelected
                      ? 'Clique novamente para desmarcar'
                      : 'Clique para selecionar'
                  }
                >
                  <td className={isSelected ? 'fw-bold text-primary' : ''}>
                    {usu.cpf}
                  </td>
                  <td className={isSelected ? 'fw-bold text-primary' : ''}>
                    {usu.nome}
                  </td>
                  <td className={isSelected ? 'fw-bold text-primary' : ''}>
                    {usu.login}
                  </td>
                  <td className={isSelected ? 'fw-bold text-primary' : ''}>
                    <span
                      className={`badge ${
                        usu.role === 'ADMIN' ? 'bg-danger' : 'bg-primary'
                      }`}
                    >
                      {usu.role === 'ADMIN' ? 'ADMINISTRADOR' : 'USUÁRIO'}
                    </span>
                  </td>
                  <td className={isSelected ? 'fw-bold text-primary' : ''}>
                    <span
                      className={`badge ${
                        usu.situacao === 'ATIVO' ? 'bg-success' : 'bg-secondary'
                      }`}
                    >
                      {usu.situacao}
                    </span>
                  </td>
                </tr>
              );
            })}
        </tbody>
      </TableContainer>

      <PageFooter>
        <Pagination10 {...setPagination} />
      </PageFooter>

      <UsuarioModal
        isOpen={isModalOpen}
        usuario={editingUsuario}
        onClose={handleCloseModal}
        onSuccess={handleModalSuccess}
      />

      <ConfirmModal {...setConfirmModal} />
      <DefaultModal {...setDefaultModal} />
        </PageContent>
      </ResponsiveShadow>
    </Layout>
  );
}

export default Usuarios;
